/**
 * The BAV filter bar (App\Livewire\Partials\FilterBar), modelled on the Vantage one.
 *
 * Used twice: as the "add filter" menu, given every field grouped, and on each
 * filter chip, given the one field it edits. Either way it only ever stages a
 * filter on the bar; the data is not re-queried until the user presses Apply.
 *
 * Metric scores come as one "score picker" entry rather than a field each:
 * the user picks the metric, then which of its scores (rank, percentage, …)
 * to filter on. Each score is still a numeric field of its own underneath.
 */
export default ({groups = [], field = null} = {}) => ({
    open: false,
    step: field ? 'configure' : 'select', // 'select' | 'metric' | 'configure'
    groups: groups,
    presetField: field,
    menuSearch: '',
    highlightedIndex: 0,

    scorePicker: null,
    metric: null,
    metricSearch: '',

    field: null,
    isEditing: false, // Whether the field already had a staged condition.
    operator: null,
    selected: [],
    value: null,
    min: null,
    max: null,

    options: [],
    optionSearch: '',
    optionsComplete: true, // False when the server only sent part of a long list.
    searchable: false,
    loading: false,
    stageTimeout: null,
    optionSearchTimeout: null,

    init() {
        this.$watch('menuSearch', () => {
            this.highlightedIndex = 0;
        });
    },

    get visibleGroups() {
        const query = this.menuSearch.trim().toLowerCase();
        let flatIndex = 0;

        return this.groups
            .map(group => ({
                label: group.label,
                fields: group.fields.filter(field => !query || this.fieldMatches(field, query)),
            }))
            .filter(group => group.fields.length > 0)
            .map(group => ({
                ...group,
                fields: group.fields.map(field => ({...field, flatIndex: flatIndex++})),
            }));
    },

    get visibleFields() {
        return this.visibleGroups.flatMap(group => group.fields);
    },

    // The score picker matches on its own label and on every metric it holds,
    // so searching the menu for a metric still finds it.
    fieldMatches(field, query) {
        if (field.label.toLowerCase().includes(query)) {
            return true;
        }

        return field.type === 'score'
            && field.groups.some(group => group.metrics.some(metric => metric.label.toLowerCase().includes(query)));
    },

    get visibleMetricGroups() {
        const query = this.metricSearch.trim().toLowerCase();

        return (this.scorePicker?.groups ?? [])
            .map(group => ({
                label: group.label,
                metrics: group.metrics.filter(metric => !query || metric.label.toLowerCase().includes(query)),
            }))
            .filter(group => group.metrics.length > 0);
    },

    get filteredOptions() {
        const query = this.optionSearch.trim().toLowerCase();

        // A partial list was already searched on the server.
        if (!query || !this.optionsComplete) {
            return this.options;
        }

        return this.options.filter(option => String(option.label).toLowerCase().includes(query));
    },

    get currentOperator() {
        return this.field?.operators.find(operator => operator.value === this.operator) ?? null;
    },

    get requiresRange() {
        return this.currentOperator?.requiresRange ?? false;
    },

    get rangeInvalid() {
        return this.requiresRange && this.isNumber(this.min) && this.isNumber(this.max) && parseFloat(this.min) > parseFloat(this.max);
    },

    get canSetNumeric() {
        if (this.requiresRange) {
            return this.isNumber(this.min) && this.isNumber(this.max) && !this.rangeInvalid;
        }

        return this.isNumber(this.value);
    },

    isNumber(value) {
        return value !== null && value !== '' && !isNaN(parseFloat(value));
    },

    openMenu() {
        this.open = true;

        if (this.presetField) {
            this.configure(this.presetField);

            return;
        }

        this.step = 'select';
        this.$nextTick(() => this.$refs.menuSearch?.focus());
    },

    close() {
        this.open = false;

        // Closing straight after ticking an option must not lose the pick.
        if (this.stageTimeout) {
            clearTimeout(this.stageTimeout);
            this.stageTimeout = null;
            this.stage(this.selected);
        }

        this.step = this.presetField ? 'configure' : 'select';
        this.field = null;
        this.scorePicker = null;
        this.metric = null;
        this.metricSearch = '';
        this.menuSearch = '';
        this.optionSearch = '';
        this.highlightedIndex = 0;
        this.options = [];
    },

    backToFields() {
        this.step = 'select';
        this.field = null;
        this.scorePicker = null;
        this.metric = null;
        this.metricSearch = '';
        this.optionSearch = '';
        this.$nextTick(() => this.$refs.menuSearch?.focus());
    },

    backToMetrics() {
        this.step = 'metric';
        this.field = null;
        this.metric = null;
        this.$nextTick(() => this.$refs.metricSearch?.focus());
    },

    back() {
        if (this.step === 'configure' && this.metric) {
            this.backToMetrics();

            return;
        }

        this.backToFields();
    },

    handleEscape() {
        if (this.presetField || this.step === 'select') {
            this.close();

            return;
        }

        this.back();
    },

    highlight(direction) {
        const count = this.visibleFields.length;

        if (count === 0) {
            return;
        }

        this.highlightedIndex = (this.highlightedIndex + direction + count) % count;

        this.$nextTick(() => {
            this.$refs.fieldList?.querySelector(`[data-field-index="${this.highlightedIndex}"]`)?.scrollIntoView({block: 'nearest'});
        });
    },

    selectHighlighted() {
        const field = this.visibleFields[this.highlightedIndex];

        if (field) {
            this.select(field);
        }
    },

    select(field) {
        if (field.type === 'score') {
            this.openScorePicker(field);

            return;
        }

        this.configure(field);
    },

    openScorePicker(picker) {
        const query = this.menuSearch.trim();

        this.scorePicker = picker;
        this.metric = null;
        // Carry a metric search over from the menu, so it is not typed twice.
        this.metricSearch = picker.label.toLowerCase().includes(query.toLowerCase()) ? '' : query;
        this.step = 'metric';

        this.$nextTick(() => this.$refs.metricSearch?.focus());
    },

    selectFirstMetric() {
        const metric = this.visibleMetricGroups[0]?.metrics[0];

        if (metric) {
            this.selectMetric(metric);
        }
    },

    selectMetric(metric) {
        this.metric = metric;
        this.configure(this.scoreField(metric.scores[0]));
    },

    // Swap to another score of the same metric, keeping what was typed.
    selectScore(score) {
        this.configure(this.scoreField(score), true);
    },

    scoreField(score) {
        return {
            key: score.key,
            label: score.label,
            type: 'numeric',
            operators: this.scorePicker.operators,
            min: score.min,
            max: score.max,
        };
    },

    async configure(field, keepValues = false) {
        this.field = field;
        this.step = 'configure';
        this.isEditing = false;
        this.options = [];
        this.optionSearch = '';
        this.selected = [];

        if (!keepValues) {
            this.operator = field.operators[0]?.value ?? null;
            this.value = null;
            this.min = null;
            this.max = null;
        }

        // Start from what is already staged, so reopening a filter shows the
        // current picks rather than a blank editor.
        const current = await this.$wire.getCurrentCondition(field.key);

        if (current) {
            this.isEditing = true;
            this.operator = current.operator;

            if (field.type === 'multiselect') {
                this.selected = current.value;
            } else if (field.type === 'numeric' && Array.isArray(current.value)) {
                [this.min, this.max] = current.value;
            } else {
                this.value = current.value;
            }
        }

        if (field.type === 'multiselect') {
            await this.loadOptions();

            this.$nextTick(() => this.$refs.optionSearch?.focus());
        }

        if (field.type === 'numeric') {
            this.$nextTick(() => this.$refs.numericValue?.focus());
        }
    },

    async loadOptions() {
        this.loading = true;

        const response = await this.$wire.getFilterOptions(this.field.key, this.optionSearch);

        this.options = response.options;
        this.searchable = response.searchable;
        this.optionsComplete = response.complete;
        this.loading = false;
    },

    // Long lists only come in part, so their search has to go to the server.
    searchOptions() {
        if (this.optionsComplete) {
            return;
        }

        clearTimeout(this.optionSearchTimeout);
        this.optionSearchTimeout = setTimeout(() => this.loadOptions(), 300);
    },

    isSelected(id) {
        return this.selected.some(selected => String(selected) === String(id));
    },

    toggleOption(id) {
        this.selected = this.isSelected(id)
            ? this.selected.filter(selected => String(selected) !== String(id))
            : [...this.selected, id];

        this.debouncedStage();
    },

    setOperator(operator) {
        this.operator = operator;

        if (this.field?.type === 'multiselect' && this.selected.length > 0) {
            this.debouncedStage();
        }
    },

    // Multiselect picks are staged as the user ticks them, batched so a run of
    // clicks only round-trips once.
    debouncedStage() {
        clearTimeout(this.stageTimeout);
        this.stageTimeout = setTimeout(() => {
            this.stageTimeout = null;
            this.stage(this.selected);
        }, 400);
    },

    setBoolean(value) {
        this.value = value;
        this.stage(value);
        this.close();
    },

    setNumeric() {
        if (!this.canSetNumeric) {
            return;
        }

        this.stage(this.requiresRange ? [parseFloat(this.min), parseFloat(this.max)] : parseFloat(this.value));
        this.close();
    },

    stage(value) {
        this.$wire.stageFilter(this.field.key, this.operator, value);
    },
});
